/* eslint-disable react/jsx-props-no-spreading */
import React, { FC, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { FormProvider, useForm } from 'react-hook-form';
import { Modal } from '../style';
import { Col, FormRow } from '../../../../UI/Grid';
import Button from '../../../../UI/Button';
import FormControlDatePicker from '../../../../UI/FormComponent/FormControlDatePicker';
import FormControlSelect from '../../../../UI/FormComponent/FormControlSelect';
import {
  MESSAGES,
  GET_ENDUR_DEAL_IDS,
  SYNC_DA_NOMS_TO_ENDUR,
} from '../../../../constants/FieldNominations';
import { IUploadDANoms } from '../../../../types/FieldNominations.type';
import { get } from '../../../../services/GenesisApi';
import { sendNotification } from '../../../../UI/Notification';
import { COMMON_MESSAGES } from '../../../../constants/Common';
import { getCurrentGasDay } from '../../../../utils/common/utils';

const PARTIALLY_SENT = 'Day Ahead Nominations partially sent to Kafka topic';
const FAILED_SENT = 'Failed to send Day Ahead Nominations to Kafka topic';

const UploadDANoms: FC<IUploadDANoms> = ({
  open,
  onClose,
  handleRefreshData,
  fromDate,
  toDate,
}) => {
  const Y_OFFSET = -32;
  const X_OFFSET = 0;
  const methods = useForm({ mode: 'onBlur' });
  const { reset, getValues } = methods;

  type IDeal = string | null;

  const [loading, setLoading] = useState<boolean>(false);
  const [loadingUpload, setLoadingUpload] = useState<boolean>(false);
  const [dealIds, setDealIds] = useState<IDeal[]>([]);

  const handleResetForm = () => {
    reset({
      fromDate: dayjs(fromDate),
      toDate: dayjs(toDate),
      dealLegIds: null,
    });
  };

  useEffect(() => {
    handleResetForm();
    setLoading(true);
    get(GET_ENDUR_DEAL_IDS)
      .then((response) => {
        const data = response.data ?? [];
        setDealIds(data);
      })
      .catch(() => {
        sendNotification({
          type: 'error',
          message: COMMON_MESSAGES.ERROR,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const handleClose = () => {
    handleResetForm();
    onClose();
  };

  const handleOnSubmit = (formData) => {
    setLoadingUpload(true);
    const params = new URLSearchParams({
      fromDate: dayjs(formData.fromDate).format('YYYY-MM-DD'),
      toDate: dayjs(formData.toDate).format('YYYY-MM-DD'),
    });
    if (formData.dealLegIds && formData.dealLegIds.length > 0) {
      params.append('dealDealLegId', formData.dealLegIds.join(','));
    }
    const uploadedDealsLength =
      formData.dealLegIds && formData.dealLegIds.length > 0
        ? formData.dealLegIds.length
        : dealIds.filter((_) => _ !== null).length;
    get(`${SYNC_DA_NOMS_TO_ENDUR}?${params.toString()}`)
      .then((response) => {
        const resData = response?.data;
        if (resData?.total?.length === 0) {
          sendNotification({
            type: 'info',
            message:
              'No Day Ahead Nominations found for the selected criteria.',
          });
          handleRefreshData();
          handleClose();
          return;
        }

        if (uploadedDealsLength === resData?.total?.length) {
          if (resData?.failed?.length === 0) {
            sendNotification({
              type: 'success',
              message:
                'Day Ahead Nominations sent to Kafka topic successfully!',
            });
          } else if (resData?.failed?.length > 0 && resData?.sent?.length > 0) {
            sendNotification({
              type: 'warning',
              message: PARTIALLY_SENT,
            });
          } else if (resData?.sent?.length === 0) {
            sendNotification({
              type: 'error',
              message: FAILED_SENT,
            });
          }
        } else if (resData?.failed?.length === 0) {
          sendNotification({
            type: 'success',
            message: PARTIALLY_SENT,
          });
        } else if (resData?.failed?.length > 0 && resData?.sent?.length > 0) {
          sendNotification({
            type: 'warning',
            message: PARTIALLY_SENT,
          });
        } else if (resData?.sent?.length === 0) {
          sendNotification({
            type: 'error',
            message: FAILED_SENT,
          });
        }
        handleRefreshData();
        handleClose();
      })
      .catch(() => {
        sendNotification({
          type: 'error',
          message: COMMON_MESSAGES.ERROR,
        });
      })
      .finally(() => {
        setLoadingUpload(false);
      });
  };

  const renderFooter = () => (
    <div className='button-style'>
      <Button
        type='button'
        onClick={handleClose}
        variant='outlined'
        data-testid='btn-cancel'
      >
        Cancel
      </Button>
      <Button
        data-testid='submit-form'
        type='button'
        loading={loadingUpload}
        disabled={loadingUpload || loading}
        onClick={methods.handleSubmit(handleOnSubmit)}
      >
        Upload
      </Button>
    </div>
  );

  return (
    <Modal
      title='Upload Day Ahead Nominations to Endur'
      open={open}
      onClose={handleClose}
      footer={renderFooter()}
      maskClosable={false}
      width={650}
    >
      <FormProvider {...methods}>
        <FormRow>
          <Col size={6}>
            <FormControlDatePicker
              controllerProps={{
                name: 'fromDate',
                rules: {
                  required: MESSAGES.REQUIRED_FROM_DATE,
                },
              }}
              inputProps={{
                placeholder: 'From Date',
                dropdownAlign: {
                  points: ['bl', 'tl'],
                  targetOffset: [X_OFFSET, Y_OFFSET],
                },
                format: 'DD MMM YYYY',
                disabledDate: (current) =>
                  getCurrentGasDay().add(4, 'day').isBefore(dayjs(current)),
              }}
              label='From Date'
              methods={methods}
            />
          </Col>
          <Col size={6}>
            <FormControlDatePicker
              controllerProps={{
                name: 'toDate',
                rules: {
                  required: MESSAGES.REQUIRED_TO_DATE,
                  validate: (value) => {
                    if (dayjs(value).isBefore(dayjs(getValues('fromDate')))) {
                      return 'To date should be greater than From date';
                    }
                    return true;
                  },
                },
              }}
              inputProps={{
                placeholder: 'To Date',
                dropdownAlign: {
                  points: ['bl', 'tl'],
                  targetOffset: [X_OFFSET, Y_OFFSET],
                },
                format: 'DD MMM YYYY',
                disabledDate: (current) =>
                  getCurrentGasDay().add(4, 'day').isBefore(dayjs(current)),
              }}
              label='To Date'
              methods={methods}
            />
          </Col>
        </FormRow>
        <FormRow>
          <Col size={12}>
            <FormControlSelect
              controllerProps={{
                name: 'dealLegIds',
              }}
              label='Deal IDs'
              isLoading={loading}
              data={dealIds
                .filter((_) => _ !== null)
                .map((deal) => ({
                  id: deal,
                  name: deal,
                }))}
              methods={methods}
              selectProps={{
                allowClear: true,
                mode: 'multiple',
                placeholder: 'Select Deal IDs',
              }}
            />
            <span className='helper-text'>
              If no Deal ID is selected, nominations for all deals within the
              selected date range will be uploaded.
            </span>
          </Col>
        </FormRow>
      </FormProvider>
    </Modal>
  );
};

export default UploadDANoms;
